import { useState } from 'react';
import { Button, LoadingStatus, Menu, Notice, ProblemPanel, Spinner, StatePanel, buttonClass } from '../../ui';
import { Link } from '../../router/Link';
import { FeedGrid, FeedGridSkeleton } from '../../listing/FeedGrid';
import { GridSearch } from '../../listing/GridSearch';
import { useAutoLoadMore } from '../../listing/useAutoLoadMore';
import { useSubscriptions } from './useSubscriptions';
import { watchedFilterMenu } from './model';
import layout from '../../ui/layout.module.css';
import styles from './subscriptions.module.css';
import { useCopy } from '../../strings/useCopy';

/**
 * §8's Subscriptions page. Renders props (R13) — every href arrived built, every string arrived
 * formatted, and the only branches are on `state.kind`.
 *
 * The channel strip that used to sit above the feed moved to the manage page (`/feed/channels`),
 * where the list is the subject rather than a bar in front of one — the Manage link in the header is
 * the way there. What stays is the feed.
 *
 * `layout.wide` is the frame, the same one Home and Trending use — and it is here rather than in
 * `GatedView` because the gate renders its children verbatim (that is its whole contract), so a page
 * behind it that sets no frame gets none: this grid sat flush against the nav and the window edge with
 * no maximum width, while the identical grid on Home was padded and centred. Two of §8's five nav
 * destinations drawing the same cards at different insets is the visible half; the reason it is the
 * *wide* frame rather than the ordinary one is `layout.module.css`'s measurement — a grid page needs
 * the width a column of prose does not.
 */
export function SubscriptionsView() {
  const copy = useCopy().text;
  const words = copy.subscriptions.page;
  const searchWords = copy.listing.search;
  const binding = useSubscriptions();
  const { feed } = binding;
  const [watchedMenuOpen, setWatchedMenuOpen] = useState(false);
  const searching = binding.search.trim() !== '';
  // Suspended while a search is set: the observer would otherwise chase matches through the whole
  // feed the moment a query empties the visible page. The button stays for a deliberate press.
  const autoLoad = useAutoLoadMore(
    feed.kind === 'ready' && feed.nextPage !== null && !binding.loadingMore && !searching,
    binding.loadMore,
  );

  return (
    <div className={layout.wide}>
      <section className={styles.page} aria-busy={feed.kind === 'loading' ? true : undefined}>
        <header className={styles.headRow}>
          <div className={styles.head}>
            <h1 className={styles.heading}>
              {words.heading}
              {binding.refreshing ? (
                <span className={styles.refreshing}>
                  <Spinner />
                </span>
              ) : null}
            </h1>
            <p className={styles.lede}>{words.lede}</p>
          </div>
          {/* A link, not a Button: the manage page is a place with a URL (middle click works). */}
          <Link className={buttonClass({ variant: 'default', size: 'sm' })} href={binding.manageHref}>
            {words.manage}
          </Link>
        </header>

        {feed.kind === 'loading' ? (
          <>
            <LoadingStatus>{words.loading}</LoadingStatus>
            <FeedGridSkeleton cards={feed.skeletonCards} />
          </>
        ) : null}

        {feed.kind === 'problem' ? <ProblemPanel copy={feed.copy} onRetry={binding.retry} /> : null}

        {feed.kind === 'empty' ? <StatePanel title={feed.title} detail={feed.detail} /> : null}

        {feed.kind === 'ready' ? (
          <>
            {feed.notices.map((notice) => (
              <Notice key={notice.id} tone={notice.tone === 'info' ? 'info' : 'caution'}>
                {notice.text}
              </Notice>
            ))}
            {/* The controls line above the grid (CLAUDE.md): the seen filter on the left,
                the search on the right. The filter is a setting rather than page state, so the
                choice holds across visits (`hideWatchedInFeed`); the search is display-only. */}
            <div className={styles.controls}>
              <Menu
                menu={watchedFilterMenu(binding.hideSeen)}
                open={watchedMenuOpen}
                onOpenChange={setWatchedMenuOpen}
                onSelect={(itemId) => {
                  setWatchedMenuOpen(false);
                  binding.setHideSeen(itemId === 'hidden');
                }}
              />
              <GridSearch
                id="search-subscriptions"
                value={binding.search}
                onChange={binding.setSearch}
                words={searchWords}
                className={styles.controlsSearch}
              />
            </div>
            {feed.nothingMatches ? (
              <p className={styles.noMatches}>{searchWords.noMatches(binding.search.trim())}</p>
            ) : (
              <FeedGrid items={feed.items.map((row) => row.item)} label={words.gridLabel} />
            )}

            {/* A finished list simply ends — the "that is all N" sentence is retired. */}
            <div className={styles.paging} ref={autoLoad}>
              {feed.nextPage === null ? null : (
                <Button onClick={binding.loadMore} disabled={binding.loadingMore}>
                  {binding.loadingMore
                    ? words.loadingMore
                    : words.loadMore(feed.listing.rows.length)}
                </Button>
              )}
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
}
